import React, { useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { components, InputActionMeta, OptionProps, StylesConfig } from "react-select";
import { useFormikContext } from "formik";
import { CustomControlIconError } from "../ReactSelect/CustomControlIconError";
import { CustomDropdownIndicator } from "../ReactSelect/CustomDropdownIndicator";
import { normalizeText } from "@me-team/host/src/utils/utils";

export interface Option {
    id?: number;
    value?: string;
    label?: string;
    name?: string;
    __isNew__?: boolean;
}

interface CustomReactSelectProps {
    optionsArray: Option[] | undefined;
    placeholder: string;
    setField: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
    fieldName: string;
    value: "id" | "value" | "name";
    isTooltip?: boolean;
    name?: string;
    id?: string;
    disabled?: boolean;
    initialValue?: Option;
    maxLength?: number;
    createOptionLabel: string;
    noOptionsText: string;
    createOnBlur?: boolean;
}

const customStyles = (error: any, touched: any, isTooltip: boolean): StylesConfig<Option, false> => ({
    control: (provided, { isFocused }) => {
        const defaultBorderColor = isFocused ? "#ACA1DF" : "#DEDEDE";
        const borderColor = touched && error ? "#DC3545" : defaultBorderColor;
        return {
            ...provided,
            borderColor: borderColor,
            padding: "6px 0px",
            borderRadius: "4px",
            minHeight: "100%",
            boxShadow: isFocused ? "0 0 0 0.2rem rgba(111, 66, 193, 0.25)" : "none",
            "&:hover": {
                borderColor: "#ACA1DF",
            },
        };
    },

    option: (provided, state) => {
        const isCreateOption = Boolean((state.data as Option).__isNew__);
        return {
            ...provided,
            borderRadius: "4px",
            marginBottom: "8px",
            padding: "4px 12px",
            cursor: "pointer",
            backgroundColor: state.isSelected ? "#5842BE" : "#FFFFFF",
            color: isCreateOption ? "#5842BE" : state.isSelected ? "#FFFFFF" : "#1F3354",
            "&:hover": {
                backgroundColor: "#EEECF9",
                color: "#1F3354",
            },
        };
    },

    placeholder: (provided) => ({
        ...provided,
        fontSize: "14px",
        color: "#9D9D9D",
        margin: "0px 4px",
    }),

    input: (provided) => ({
        ...provided,
        fontSize: "14px",
        color: "#1F3354",
    }),

    dropdownIndicator: (provided, { selectProps }) => ({
        ...provided,
        transform: selectProps.menuIsOpen ? "rotate(180deg)" : null,
        transition: "transform 0.2s ease",
        marginRight: isTooltip ? "26.26px" : undefined,
        padding: "8px 12px",
        color: "#1F3354",
    }),

    menu: (provided) => ({
        ...provided,
        fontSize: "14px",
        marginTop: "4px",
    }),

    menuList: (provided) => ({
        ...provided,
        padding: "8px 12px",
    }),

    indicatorSeparator: () => ({
        display: "none",
    }),

    singleValue: (provided) => ({
        ...provided,
        fontSize: "14px",
    }),
});

const SelectOption = (props: OptionProps<Option, false>) => {
    const isCreateOption = Boolean((props.data as Option).__isNew__);
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center gap-2">
                {isCreateOption && (
                    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0">
                        <i className="bi bi-plus-lg text-primary lh-1" />
                    </span>
                )}

                <span className={`fs-16 ${isCreateOption ? "text-primary" : ""}`}>{props.label}</span>
            </div>
        </components.Option>
    );
};

const CustomCreatableReactSelect: React.FC<CustomReactSelectProps> = ({
    optionsArray,
    placeholder,
    setField,
    fieldName,
    value,
    isTooltip,
    initialValue,
    name,
    id,
    disabled = false,
    maxLength,
    createOptionLabel,
    noOptionsText,
    createOnBlur = true,
}) => {
    const [inputValue, setInputValue] = useState<string>("");
    const formikContext = useFormikContext<any>();
    const errors = formikContext?.errors && formikContext.errors[fieldName];
    const touched = formikContext?.touched && formikContext.touched[fieldName];

    const transformedOptions: Option[] = useMemo(() => {
        return (
            optionsArray?.map((optionItem) => ({
                id: optionItem?.id,
                value: optionItem?.name,
                label: optionItem?.name,
                name: optionItem?.name,
            })) ?? []
        );
    }, [optionsArray]);

    const currentFieldValue = formikContext.values[fieldName];

    const selectedOption: Option | null = useMemo(() => {
        if (currentFieldValue === null || currentFieldValue === undefined || currentFieldValue === "") {
            return initialValue ?? null;
        }
        if (value === "id") {
            return transformedOptions.find((optionItem) => optionItem.id === currentFieldValue) ?? null;
        }
        const stringValue = String(currentFieldValue);
        return (
            transformedOptions.find((optionItem) => optionItem.value === stringValue) ?? {
                label: stringValue,
                value: stringValue,
            }
        );
    }, [currentFieldValue, initialValue, transformedOptions, value]);

    const writeFormikValue = (optionToWrite: Option | null) => {
        if (!optionToWrite) {
            setField(fieldName, null, true);
            return;
        }
        if (value === "id") {
            setField(fieldName, optionToWrite.id ?? null, true);
            return;
        }
        setField(fieldName, optionToWrite.value ?? null, true);
    };

    const handleChange = (newSelectedOption: Option | null) => {
        writeFormikValue(newSelectedOption);
        formikContext.setFieldTouched(fieldName, true, false);
        setInputValue("");
    };

    const createAndSelect = (raw: string) => {
        const normalized = normalizeText(raw);

        if (!normalized) return;

        if (maxLength && normalized.length > maxLength) {
            const sliced = normalized.slice(0, maxLength);
            if (!sliced) return;
        }

        const normalizedLower = normalized.toLowerCase();

        const existingOption =
            transformedOptions.find(
                (optionItem) => normalizeText(optionItem.value ?? "").toLowerCase() === normalizedLower
            ) ?? null;

        const optionToSelect: Option = existingOption ?? { label: normalized, value: normalized, name: normalized };

        handleChange(optionToSelect);
    };

    const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
        if (actionMeta.action !== "input-change") return newValue;

        let nextValue = newValue;

        if (maxLength && nextValue.length > maxLength) {
            nextValue = nextValue.slice(0, maxLength);
        }

        setInputValue(nextValue);
        return nextValue;
    };

    const handleBlur = () => {
        if (!createOnBlur) return;
        const normalized = normalizeText(inputValue);
        if (!normalized) return;
        createAndSelect(normalized);
    };

    const formatCreateLabel = (raw: string) => {
        const normalized = normalizeText(raw);
        return `${createOptionLabel} "${normalized}"`;
    };

    return (
        <CreatableSelect
            placeholder={placeholder}
            styles={customStyles(errors, touched, isTooltip)}
            options={transformedOptions}
            onChange={handleChange}
            onBlur={handleBlur}
            onCreateOption={createAndSelect}
            createOptionPosition="first"
            value={selectedOption}
            formatCreateLabel={formatCreateLabel}
            noOptionsMessage={() => noOptionsText}
            inputId={name}
            id={id}
            name={name}
            isDisabled={disabled}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            components={{
                Control: CustomControlIconError,
                DropdownIndicator: CustomDropdownIndicator,
                Option: SelectOption,
            }}
            isValidNewOption={(raw, _selected, options) => {
                const normalized = normalizeText(raw);
                if (!normalized) return false;

                const normalizedLower = normalized.toLowerCase();
                return !options.some((optionItem) => {
                    const optionValue = normalizeText((optionItem as Option)?.value ?? "");
                    return optionValue.toLowerCase() === normalizedLower;
                });
            }}
            {...{
                error: touched && errors,
            }}
        />
    );
};

export default CustomCreatableReactSelect;
